import { useCallback, useEffect, useMemo, useState } from "react";
import { streamAgentChat, streamAgentApprove, fetchLiveSession } from "./mockupEditorApi";
import { compileLiveComponent } from "./compileLiveComponent";

const tryCompile = async (source, { hillKey } = {}) => {
  try {
    const Component = await compileLiveComponent(source, { hillKey });
    return { Component, error: null };
  } catch (err) {
    return { Component: null, error: err.message };
  }
};

const compileOverrides = async (sourceMap, { hillKey } = {}) => {
  const entries = await Promise.all(
    Object.entries(sourceMap).map(async ([route, entry]) => [route, await tryCompile(entry.source, { hillKey })]),
  );
  return Object.fromEntries(entries);
};

const appendOrExtendAssistantMessage = (prev, text) => {
  const last = prev[prev.length - 1];
  const shouldExtend = Boolean(last && last.role === "assistant" && last.streaming);
  const extended = shouldExtend ? { ...last, text: last.text + text } : { role: "assistant", text, streaming: true };
  const base = shouldExtend ? prev.slice(0, -1) : prev;
  return [...base, extended];
};

const closeAssistantMessage = (prev) => {
  const last = prev[prev.length - 1];
  const shouldClose = Boolean(last && last.role === "assistant" && last.streaming);
  return shouldClose ? [...prev.slice(0, -1), { ...last, streaming: false }] : prev;
};

const TOOL_STATUS_LABELS = {
  mcp__mockup_editor__get_screen_source: "Reading the screen's code…",
  mcp__mockup_editor__preview_change: "Applying your change…",
  mcp__mockup_editor__record_deferred_requirement: "Noting this for the build phase…",
  mcp__mockup_editor__apply_principle_across_hill: "Applying this across the hill…",
  mcp__mockup_editor__open_github_pr: "Opening a pull request…",
};

const toolStatusLabel = (toolName) => TOOL_STATUS_LABELS[toolName] || "Working…";

const useMockupEditorChat = ({
  hillId,
  hillKey,
  knowledgeDocId,
  scenarioId,
  scenarioName,
  screenRoute,
  screenTitle,
}) => {
  const [messages, setMessages] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [pendingApproval, setPendingApproval] = useState(null);
  const [sending, setSending] = useState(false);
  const [toolStatus, setToolStatus] = useState(null);
  const [error, setError] = useState(null);
  const [liveOverrides, setLiveOverrides] = useState({});

  const pageContext = useMemo(
    () => ({ hillId, hillKey, knowledgeDocId, scenarioId, scenarioName, screenRoute, screenTitle }),
    [hillId, hillKey, knowledgeDocId, scenarioId, scenarioName, screenRoute, screenTitle],
  );

  const refreshLiveSession = useCallback(async () => {
    if (!hillId) return;
    try {
      const { pendingOverrides } = await fetchLiveSession({ hillId });
      const compiled = await compileOverrides(pendingOverrides, { hillKey });
      setLiveOverrides(compiled);
    } catch (err) {
      console.warn("[useMockupEditorChat] failed to refresh live session:", err.message);
    }
  }, [hillId, hillKey]);

  useEffect(() => {
    refreshLiveSession();
  }, [refreshLiveSession]);

  const handleStreamEvent = useCallback(
    (event) => {
      if (event.threadId) {
        setThreadId(event.threadId);
      }
      if (event.token) {
        setMessages((prev) => appendOrExtendAssistantMessage(prev, event.token));
      }
      if (event.tool_use) {
        setToolStatus(toolStatusLabel(event.tool_use.name));
      }
      if (event.tool_result) {
        setToolStatus(null);
        refreshLiveSession();
      }
      if (event.pending_approval) {
        setPendingApproval(event.pending_approval);
      }
      if (event.error) {
        setError(event.error);
      }
      if (event.done) {
        setMessages((prev) => closeAssistantMessage(prev));
        setToolStatus(null);
        setSending(false);
      }
    },
    [refreshLiveSession],
  );

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      setMessages((prev) => [...prev, { role: "reviewer", text: trimmed }]);
      setSending(true);
      setToolStatus(null);
      setError(null);
      try {
        await streamAgentChat({ threadId, message: trimmed, pageContext, onEvent: handleStreamEvent });
      } catch (err) {
        setError(err.message);
        setToolStatus(null);
        setSending(false);
      }
    },
    [threadId, pageContext, handleStreamEvent],
  );

  const respondToApproval = useCallback(
    async (approved, rejectionReason) => {
      if (!pendingApproval) return;
      const { id: pendingId } = pendingApproval;
      setPendingApproval(null);
      setSending(true);
      setToolStatus(null);
      setError(null);
      try {
        await streamAgentApprove({ pendingId, approved, rejectionReason, onEvent: handleStreamEvent });
      } catch (err) {
        setError(err.message);
        setToolStatus(null);
        setSending(false);
      }
    },
    [pendingApproval, handleStreamEvent],
  );

  return { messages, sending, toolStatus, error, pendingApproval, liveOverrides, sendMessage, respondToApproval };
};

export { useMockupEditorChat };
